/**
 * AudioManager - 使用 Web Audio API 合成遊戲音效
 * 不需要真實音效檔案，全部使用 OscillatorNode + GainNode 合成
 */

import { getLogger } from '../utils/logger';

const log = getLogger('AudioManager');

export type SoundEffect =
  | 'drawCard'       // 抽牌音效（清脆翻牌聲）
  | 'playCard'       // 出牌音效（放下聲）
  | 'attack'         // 攻擊音效（衝擊聲）
  | 'damage'         // 受傷音效（打擊聲）
  | 'destroy'        // 破壞音效（爆炸聲）
  | 'buttonClick'    // 按鈕音效（點擊聲）
  | 'phaseChange';   // 階段轉換音效（短促提示音）

class AudioManagerClass {
  private context: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private _volume = 0.5;
  private _muted = false;

  /** 取得或建立 AudioContext（需要用戶互動後才能使用） */
  private getContext(): AudioContext | null {
    if (!this.context) {
      try {
        this.context = new AudioContext();
        this.masterGain = this.context.createGain();
        this.masterGain.gain.value = this._muted ? 0 : this._volume;
        this.masterGain.connect(this.context.destination);
      } catch {
        log.warn('Web Audio API 不可用');
        return null;
      }
    }

    // 恢復被暫停的 context
    if (this.context.state === 'suspended') {
      void this.context.resume();
    }

    return this.context;
  }

  /** 音量 (0-1) */
  get volume(): number {
    return this._volume;
  }

  set volume(value: number) {
    this._volume = Math.max(0, Math.min(1, value));
    if (this.masterGain && !this._muted) {
      this.masterGain.gain.value = this._volume;
    }
  }

  /** 是否靜音 */
  get muted(): boolean {
    return this._muted;
  }

  set muted(value: boolean) {
    this._muted = value;
    if (this.masterGain) {
      this.masterGain.gain.value = value ? 0 : this._volume;
    }
  }

  /** 切換靜音 */
  toggleMute(): void {
    this.muted = !this._muted;
  }

  /** 播放指定音效 */
  play(effect: SoundEffect): void {
    const ctx = this.getContext();
    const master = this.masterGain;
    if (!ctx || !master) return;

    switch (effect) {
      case 'drawCard':
        this.playDrawCard(ctx, master);
        break;
      case 'playCard':
        this.playPlayCard(ctx, master);
        break;
      case 'attack':
        this.playAttack(ctx, master);
        break;
      case 'damage':
        this.playDamage(ctx, master);
        break;
      case 'destroy':
        this.playDestroy(ctx, master);
        break;
      case 'buttonClick':
        this.playButtonClick(ctx, master);
        break;
      case 'phaseChange':
        this.playPhaseChange(ctx, master);
        break;
    }
  }

  // ================================================================
  // 各種合成音效實作
  // ================================================================

  /** 抽牌音效 - 清脆翻牌聲（快速上升的高頻音） */
  private playDrawCard(ctx: AudioContext, master: GainNode): void {
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(800, now);
    osc.frequency.exponentialRampToValueAtTime(2000, now + 0.08);

    gain.gain.setValueAtTime(0.3, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.12);

    osc.connect(gain);
    gain.connect(master);
    osc.start(now);
    osc.stop(now + 0.15);

    // 加入第二個音（翻頁感）
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'triangle';
    osc2.frequency.setValueAtTime(1200, now + 0.03);
    osc2.frequency.exponentialRampToValueAtTime(3000, now + 0.1);
    gain2.gain.setValueAtTime(0.15, now + 0.03);
    gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.13);
    osc2.connect(gain2);
    gain2.connect(master);
    osc2.start(now + 0.03);
    osc2.stop(now + 0.15);
  }

  /** 出牌音效 - 放下聲（低沉的 thud） */
  private playPlayCard(ctx: AudioContext, master: GainNode): void {
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(300, now);
    osc.frequency.exponentialRampToValueAtTime(80, now + 0.15);

    gain.gain.setValueAtTime(0.4, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.2);

    osc.connect(gain);
    gain.connect(master);
    osc.start(now);
    osc.stop(now + 0.25);

    // 噪音感
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sawtooth';
    osc2.frequency.setValueAtTime(150, now);
    osc2.frequency.exponentialRampToValueAtTime(50, now + 0.1);
    gain2.gain.setValueAtTime(0.1, now);
    gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.12);
    osc2.connect(gain2);
    gain2.connect(master);
    osc2.start(now);
    osc2.stop(now + 0.15);
  }

  /** 攻擊音效 - 衝擊聲（快速下降的噪音 + 低頻衝擊） */
  private playAttack(ctx: AudioContext, master: GainNode): void {
    const now = ctx.currentTime;

    // 衝擊主音
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(400, now);
    osc.frequency.exponentialRampToValueAtTime(100, now + 0.15);
    gain.gain.setValueAtTime(0.3, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
    osc.connect(gain);
    gain.connect(master);
    osc.start(now);
    osc.stop(now + 0.25);

    // 低頻衝擊
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(120, now);
    osc2.frequency.exponentialRampToValueAtTime(40, now + 0.2);
    gain2.gain.setValueAtTime(0.5, now);
    gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.25);
    osc2.connect(gain2);
    gain2.connect(master);
    osc2.start(now);
    osc2.stop(now + 0.3);
  }

  /** 受傷音效 - 打擊聲（短促中頻衝擊） */
  private playDamage(ctx: AudioContext, master: GainNode): void {
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'square';
    osc.frequency.setValueAtTime(250, now);
    osc.frequency.exponentialRampToValueAtTime(80, now + 0.08);
    gain.gain.setValueAtTime(0.25, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
    osc.connect(gain);
    gain.connect(master);
    osc.start(now);
    osc.stop(now + 0.12);

    // 二次打擊
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(180, now + 0.05);
    osc2.frequency.exponentialRampToValueAtTime(60, now + 0.12);
    gain2.gain.setValueAtTime(0.2, now + 0.05);
    gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
    osc2.connect(gain2);
    gain2.connect(master);
    osc2.start(now + 0.05);
    osc2.stop(now + 0.18);
  }

  /** 破壞音效 - 爆炸聲（多層頻率下降 + 噪音） */
  private playDestroy(ctx: AudioContext, master: GainNode): void {
    const now = ctx.currentTime;

    // 爆炸主音
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(600, now);
    osc.frequency.exponentialRampToValueAtTime(30, now + 0.4);
    gain.gain.setValueAtTime(0.35, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
    osc.connect(gain);
    gain.connect(master);
    osc.start(now);
    osc.stop(now + 0.55);

    // 高頻碎片感
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'square';
    osc2.frequency.setValueAtTime(1500, now);
    osc2.frequency.exponentialRampToValueAtTime(200, now + 0.2);
    gain2.gain.setValueAtTime(0.15, now);
    gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.25);
    osc2.connect(gain2);
    gain2.connect(master);
    osc2.start(now);
    osc2.stop(now + 0.3);

    // 低頻震動
    const osc3 = ctx.createOscillator();
    const gain3 = ctx.createGain();
    osc3.type = 'sine';
    osc3.frequency.setValueAtTime(60, now);
    osc3.frequency.exponentialRampToValueAtTime(20, now + 0.4);
    gain3.gain.setValueAtTime(0.4, now);
    gain3.gain.exponentialRampToValueAtTime(0.01, now + 0.45);
    osc3.connect(gain3);
    gain3.connect(master);
    osc3.start(now);
    osc3.stop(now + 0.5);
  }

  /** 按鈕音效 - 點擊聲（短促清脆的 click） */
  private playButtonClick(ctx: AudioContext, master: GainNode): void {
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(1000, now);
    osc.frequency.exponentialRampToValueAtTime(800, now + 0.05);

    gain.gain.setValueAtTime(0.2, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.06);

    osc.connect(gain);
    gain.connect(master);
    osc.start(now);
    osc.stop(now + 0.08);
  }

  /** 階段轉換音效 - 短促提示音（兩段上升音） */
  private playPhaseChange(ctx: AudioContext, master: GainNode): void {
    const now = ctx.currentTime;

    // 第一個音
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(500, now);
    gain1.gain.setValueAtTime(0.2, now);
    gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
    osc1.connect(gain1);
    gain1.connect(master);
    osc1.start(now);
    osc1.stop(now + 0.12);

    // 第二個音（高八度）
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(750, now + 0.1);
    gain2.gain.setValueAtTime(0.2, now + 0.1);
    gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
    osc2.connect(gain2);
    gain2.connect(master);
    osc2.start(now + 0.1);
    osc2.stop(now + 0.22);
  }
}

/** 全域音效管理器單例 */
export const AudioManager = new AudioManagerClass();
